import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from "react";
import { X, Save, Plus, Loader2, ArrowUpDown } from "lucide-react";
import { Button } from "primereact/button";
import { fetchKeywords, updateKeywords } from "@/core/api/api";
// old code for api call
// const fetchKeywords = async (): Promise<Document[]> => {
//   try {
//     const response = await fetch(
//       `${process.env.VITE_BASE_API_URL}/settings/keywords`
//     );
//     if (!response.ok) throw new Error("Failed to fetch keywords");
//     return await response.json();
//   } catch (error) {
//     console.error("Error fetching keywords:", error);
//     throw error;
//   }
// };
// old code for api call
// const updateKeywords = async (
//   id: string,
//   keywords: Keyword[]
// ): Promise<Document> => {
//   try {
//     const response = await fetch(
//       `${process.env.VITE_BASE_API_URL}/settings/keywords/${id}`,
//       {
//         method: "PUT",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ keywords }),
//       }
//     );
//     if (!response.ok) throw new Error("Failed to update keywords");
//     return await response.json();
//   } catch (error) {
//     console.error("Error updating keywords:", error);
//     throw error;
//   }
// };
const KeywordsManager = () => {
    const [documents, setDocuments] = useState([]);
    const [editingDocId, setEditingDocId] = useState(null);
    const [editingKeywordIndex, setEditingKeywordIndex] = useState(null);
    const [editingKeyword, setEditingKeyword] = useState("");
    const [editingScore, setEditingScore] = useState("");
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newKeyword, setNewKeyword] = useState("");
    const [newScore, setNewScore] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sortOrder, setSortOrder] = useState(null);
    useEffect(() => {
        loadKeywords();
    }, []);
    const loadKeywords = async () => {
        try {
            setIsLoading(true);
            setError(null);
            let data = [];
            const { responseData, error } = await fetchKeywords();
            if (error) {
                console.error("Error fetching keywords:", error);
                setError(error.errorMessage);
            }
            else {
                if (!responseData) {
                    console.error("Error fetching keywords:", responseData);
                    setError("Failed to fetch keywords");
                }
                else {
                    data = responseData;
                }
            }
            // const data = await fetchKeywords();
            setDocuments(data);
        }
        catch (error) {
            setError("Failed to load keywords. Please try again later.");
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleEdit = (docId, keywordIndex, keyword) => {
        setEditingDocId(docId);
        setEditingKeywordIndex(keywordIndex);
        setEditingKeyword(keyword.keyword);
        setEditingScore(keyword.score.toString());
    };
    const handleSort = () => {
        setSortOrder((current) => {
            if (current === null)
                return "asc";
            if (current === "asc")
                return "desc";
            return null;
        });
    };
    const getSortedKeywords = (keywords) => {
        if (!sortOrder)
            return keywords;
        return [...keywords].sort((a, b) => {
            if (sortOrder === "asc") {
                return a.score - b.score;
            }
            return b.score - a.score;
        });
    };
    const isValidScore = (score) => {
        const numScore = parseInt(score);
        return !isNaN(numScore) && numScore >= 0 && numScore <= 100;
    };
    const handleSave = async (docId) => {
        try {
            setIsSubmitting(true);
            const doc = documents.find((d) => d._id === docId);
            if (!doc || editingKeywordIndex === null)
                return;
            const updatedKeywords = [...doc.keywords];
            updatedKeywords[editingKeywordIndex] = {
                keyword: editingKeyword,
                score: parseInt(editingScore, 10),
            };
            await updateKeywords(docId, updatedKeywords);
            setDocuments((prevDocs) => prevDocs.map((doc) => doc._id === docId ? { ...doc, keywords: updatedKeywords } : doc));
            handleCancel();
        }
        catch (error) {
            console.error("Error updating keyword:", error);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleDelete = async (docId, keywordIndex) => {
        try {
            setIsSubmitting(true);
            const doc = documents.find((d) => d._id === docId);
            if (!doc)
                return;
            const updatedKeywords = doc.keywords.filter((_, idx) => idx !== keywordIndex);
            setDocuments((prevDocs) => prevDocs.map((doc) => doc._id === docId ? { ...doc, keywords: updatedKeywords } : doc));
            await updateKeywords(docId, updatedKeywords);
        }
        catch (error) {
            console.error("Error deleting keyword:", error);
            await loadKeywords();
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleAddNew = async () => {
        try {
            setIsSubmitting(true);
            const firstDoc = documents[0];
            if (!firstDoc)
                return;
            const updatedKeywords = [
                ...firstDoc.keywords,
                {
                    keyword: newKeyword,
                    score: parseInt(newScore, 10),
                },
            ];
            await updateKeywords(firstDoc._id, updatedKeywords);
            setDocuments((prevDocs) => prevDocs.map((doc) => doc._id === firstDoc._id ? { ...doc, keywords: updatedKeywords } : doc));
            setNewKeyword("");
            setNewScore("");
            setIsAddingNew(false);
        }
        catch (error) {
            console.error("Error adding new keyword:", error);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleCancel = () => {
        setEditingDocId(null);
        setEditingKeywordIndex(null);
        setEditingKeyword("");
        setEditingScore("");
    };
    if (isLoading) {
        return (_jsx("div", { className: "flex items-center justify-center min-h-[200px]", children: _jsx(Loader2, { className: "h-8 w-8 animate-spin text-blue-500" }) }));
    }
    if (error) {
        return (_jsxs("div", { className: "max-w-2xl mx-auto p-6 text-center", children: [_jsx("div", { className: "bg-red-50 text-red-800 rounded-lg p-4 mb-4", children: error }), _jsx("button", { onClick: loadKeywords, className: "text-blue-600 hover:text-blue-800 font-medium", children: "Try Again" })] }));
    }
    return (_jsxs("div", { className: "max-w-full mx-auto bg-white rounded-lg h-full flex flex-col justify-between", children: [_jsx("h3", { className: "text-lg text-[#101828] font-medium", children: "Keyword Manager" }), _jsxs("div", { className: "w-full overflow-y-auto h-[calc(100%-60px)] relative", children: [_jsxs("div", { className: "flex items-center p-3 bg-gray-50 rounded-t-lg border-b sticky top-0", children: [_jsx("span", { className: "flex-1 font-medium text-gray-600", children: "Keyword" }), _jsxs("div", { className: "w-24 flex items-center justify-end mr-2", children: [_jsx("span", { className: "font-medium text-gray-600 mr-2", children: "Score" }), _jsx("button", { onClick: handleSort, className: `p-1 rounded hover:bg-gray-200 ${sortOrder ? "text-blue-600" : "text-gray-400"}`, title: sortOrder === "asc"
                                            ? "Sort Descending"
                                            : sortOrder === "desc"
                                                ? "Clear Sort"
                                                : "Sort Ascending", children: _jsx(ArrowUpDown, { size: 16 }) })] }), _jsx("span", { className: "w-28 text-right font-medium text-gray-600", children: "Actions" })] }), _jsx("div", { className: "flex-1 overflow-y-auto", children: _jsx("div", { className: "divide-y divide-gray-100", children: documents.map((doc) => getSortedKeywords(doc.keywords).map((keyword, keywordIndex) => (_jsx("div", { className: "flex items-center p-3 hover:bg-gray-50 transition-colors", children: editingDocId === doc._id &&
                                    editingKeywordIndex === keywordIndex ? (_jsxs(_Fragment, { children: [_jsx("input", { type: "text", value: editingKeyword, onChange: (e) => setEditingKeyword(e.target.value), className: "flex-1 px-3 py-1.5 mr-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500", placeholder: "Keyword" }), _jsx("input", { type: "number", min: "0", max: "100", value: editingScore, onChange: (e) => {
                                                const value = parseInt(e.target.value);
                                                if (isNaN(value)) {
                                                    setEditingScore("");
                                                }
                                                else {
                                                    setEditingScore(Math.min(Math.max(value, 0), 100).toString());
                                                }
                                            }, className: "w-24 px-3 py-1.5 mr-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500", placeholder: "0-100" }), _jsxs("div", { className: "w-28 flex justify-end", children: [_jsx(Button, { onClick: () => handleSave(doc._id), disabled: !editingKeyword ||
                                                        !isValidScore(editingScore) ||
                                                        isSubmitting, className: `p-1.5 rounded-md mr-1 ${!editingKeyword ||
                                                        !isValidScore(editingScore) ||
                                                        isSubmitting
                                                        ? "text-gray-400 cursor-not-allowed"
                                                        : "text-green-600 hover:text-green-800 hover:bg-green-50"}`, children: _jsx(Save, { size: 18 }) }), _jsx(Button, { onClick: handleCancel, disabled: isSubmitting, className: "p-1.5 rounded-md text-red-600 hover:text-red-800 hover:bg-red-50", children: _jsx(X, { size: 18 }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "flex-1 text-gray-900", children: keyword.keyword }), _jsx("span", { className: "w-24 text-right mr-2 text-gray-900", children: keyword.score }), _jsxs("div", { className: "w-28 flex justify-end", children: [_jsx("button", { onClick: () => handleEdit(doc._id, keywordIndex, keyword), disabled: isSubmitting, className: "px-2.5 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md mr-1", children: "Edit" }), _jsx("button", { onClick: () => handleDelete(doc._id, keywordIndex), disabled: isSubmitting, className: "px-2.5 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md", children: "Delete" })] })] })) }, `${doc._id}-${keywordIndex}`)))) }) }), _jsx("div", { className: "pt-0 sticky bottom-0 bg-white border-t mt-auto", children: isAddingNew ? (_jsx("div", { className: "p-3 bg-blue-50 rounded-md", children: _jsxs("div", { className: "flex items-center", children: [_jsx("input", { type: "text", value: newKeyword, onChange: (e) => setNewKeyword(e.target.value), className: "flex-1 px-3 py-1.5 mr-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500", placeholder: "New keyword" }), _jsx("input", { type: "number", min: "0", max: "100", value: newScore, onChange: (e) => {
                                            const value = parseInt(e.target.value);
                                            if (isNaN(value)) {
                                                setNewScore("");
                                            }
                                            else {
                                                setNewScore(Math.min(Math.max(value, 0), 100).toString());
                                            }
                                        }, className: "w-24 px-3 py-1.5 mr-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500", placeholder: "0-100" }), _jsxs("div", { className: "w-28 flex justify-end", children: [_jsx(Button, { onClick: handleAddNew, disabled: !newKeyword || !isValidScore(newScore) || isSubmitting, className: `p-1.5 rounded-md mr-1 ${!newKeyword || !isValidScore(newScore) || isSubmitting
                                                    ? "text-gray-400 cursor-not-allowed"
                                                    : "text-green-600 hover:text-green-800 hover:bg-green-50"}`, children: _jsx(Save, { size: 18 }) }), _jsx(Button, { onClick: () => {
                                                    setIsAddingNew(false);
                                                    setNewKeyword("");
                                                    setNewScore("");
                                                }, disabled: isSubmitting, className: "p-1.5 rounded-md text-red-600 hover:text-red-800 hover:bg-red-50", children: _jsx(X, { size: 18 }) })] })] }) })) : (_jsxs(Button, { onClick: () => setIsAddingNew(true), disabled: isSubmitting, className: "w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md", children: [_jsx(Plus, { size: 18, className: "mr-1" }), " Add New Keyword"] })) })] })] }));
};
export default KeywordsManager;
